import { useEffect, useRef, useState } from 'react';

import { promptText } from '../../lib/prompt-dialog.js';
import {
  addPattern,
  beatsPerBar,
  deletePattern,
  duplicatePattern,
  GM_DRUMS,
  gmDrumKey,
  laneSteps,
  noteNameToMidi,
  stepsPerBar,
  stepVelocity,
  withLaneSteps,
  parseSteps,
  patternStepCount,
  renamePattern,
  resizeSteps,
  setDrumRow,
  setStep,
  timeSignatureOf,
  updatePattern,
  type StepValue,
} from '../../lib/sound-doc.js';
import { soundPlayer } from '../../lib/sound-player.js';
import { logEditor } from '../../state/console.js';
import { useSound } from '../../state/sound.js';
import { MenuButton, type MenuItem } from '../ui/controls.js';
import { Icon } from '../ui/icons.js';

/**
 * Drum step editor for a pattern's `drums` rows (`"kick": "x...x..."`,
 * steps per bar from `pattern.steps`, default 4 per beat; `x` hit, `X` accent, `-` ghost). Click or drag to paint
 * hits; Shift paints accents, Alt paints ghosts.
 */

/** Pattern menu shared by the piano roll and the step sequencer. */
export function PatternPicker() {
  const doc = useSound((s) => s.doc);
  const name = useSound((s) => s.activePattern);
  const view = useSound((s) => s.view);
  const names = Object.keys(doc?.patterns ?? {});
  const s = useSound.getState();
  const items = (): MenuItem[] => {
    const cur = useSound.getState().doc;
    if (!cur) return [];
    return [
      { id: 'h', label: 'Patterns', heading: true },
      ...names.map((n) => ({ id: `p:${n}`, label: n, icon: 'audio' as const, run: () => s.select({ kind: 'pattern', name: n }) })),
      ...(names.length === 0 ? [{ id: 'none', label: 'No patterns yet', disabled: true }] : []),
      { id: 'sep', label: '', separator: true },
      {
        id: 'new',
        label: 'New note pattern',
        icon: 'plus',
        run: () => {
          const r = addPattern(cur, 'pattern');
          s.update(r.doc, { kind: 'pattern', name: r.name });
          s.openPattern(r.name, 'pianoRoll');
        },
      },
      {
        id: 'newdrum',
        label: 'New drum pattern',
        icon: 'grid',
        run: () => {
          const ts = timeSignatureOf(cur);
          const r = addPattern(cur, 'drums', {
            length: beatsPerBar(ts),
            drums: { kick: 'x...x...x...x...', snare: '....x.......x...', hat: 'x.x.x.x.x.x.x.x.' },
          });
          s.update(r.doc, { kind: 'pattern', name: r.name });
          s.openPattern(r.name, 'steps');
        },
      },
      ...(name
        ? [
            { id: 'sep2', label: '', separator: true },
            {
              id: 'rename',
              label: `Rename "${name}"…`,
              icon: 'edit' as const,
              run: async () => {
                const next = (await promptText('New pattern name', name))?.trim();
                const latest = useSound.getState().doc;
                if (!next || !latest || latest.patterns?.[next]) return;
                s.update(renamePattern(latest, name, next), { kind: 'pattern', name: next });
                s.openPattern(next, useSound.getState().view);
              },
            },
            {
              id: 'dup',
              label: `Duplicate "${name}"`,
              icon: 'copy' as const,
              run: () => {
                const r = duplicatePattern(cur, name);
                s.update(r.doc, { kind: 'pattern', name: r.name });
                s.openPattern(r.name, useSound.getState().view);
              },
            },
            { id: 'del', label: `Delete "${name}" and its clips`, icon: 'trash' as const, danger: true, run: () => s.update(deletePattern(cur, name), null) },
          ]
        : []),
    ];
  };
  return (
    <>
      <MenuButton className="picker" title="Open, create or rename a pattern" items={items}>
        <Icon name="audio" size={13} />
        <span className="picker-label">{name ?? 'Pattern…'}</span>
        <Icon name="chevronDown" size={11} className="muted" />
      </MenuButton>
      {name && (
        <div className="tb-group segmented" role="tablist" aria-label="Pattern editor">
          <button type="button" role="tab" aria-selected={view === 'pianoRoll'} className={`tb-btn${view === 'pianoRoll' ? ' is-active' : ''}`} onClick={() => s.setView('pianoRoll')}>
            Notes
          </button>
          <button type="button" role="tab" aria-selected={view === 'steps'} className={`tb-btn${view === 'steps' ? ' is-active' : ''}`} onClick={() => s.setView('steps')}>
            Steps
          </button>
        </div>
      )}
    </>
  );
}

interface Paint {
  value: StepValue;
  cells: Map<string, Set<number>>;
}

export function StepSequencer() {
  const doc = useSound((s) => s.doc);
  const name = useSound((s) => s.activePattern);
  const [paint, setPaint] = useState<Paint | null>(null);
  const paintRef = useRef<Paint | null>(null);
  const [newRow, setNewRow] = useState('');

  useEffect(() => {
    const up = () => {
      const p = paintRef.current;
      paintRef.current = null;
      setPaint(null);
      if (!p) return;
      const s = useSound.getState();
      const cur = s.doc;
      const pname = s.activePattern;
      const pattern = pname ? cur?.patterns?.[pname] : undefined;
      if (!cur || !pname || !pattern) return;
      const ts = timeSignatureOf(cur);
      const per = stepsPerBar(pattern, ts);
      const count = patternStepCount(pattern, ts);
      let next = cur;
      for (const [row, cells] of p.cells) {
        let steps = resizeSteps(laneSteps(pattern.drums?.[row]), count, per);
        for (const i of cells) steps = setStep(steps, i, p.value, count, per);
        next = setDrumRow(next, pname, row, steps);
      }
      if (next !== cur) s.update(next);
    };
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
    return () => {
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
    };
  }, []);

  if (!doc) return null;
  const pattern = name ? doc.patterns?.[name] : undefined;
  if (!name || !pattern) {
    return (
      <div className="snd-steps">
        <div className="snd-view-tools">
          <PatternPicker />
        </div>
        <div className="pane-empty muted">No pattern open. Create a drum pattern from the pattern menu.</div>
      </div>
    );
  }
  const s = useSound.getState();
  const ts = timeSignatureOf(doc);
  const per = stepsPerBar(pattern, ts);
  const perBeat = Math.max(1, Math.round(per / beatsPerBar(ts)));
  const count = patternStepCount(pattern, ts);
  const bars = count / per;
  const rows = Object.entries(pattern.drums ?? {});
  const unused = Object.keys(GM_DRUMS).filter((k) => !(pattern.drums && k in pattern.drums));

  const addCell = (row: string, i: number) => {
    const p = paintRef.current;
    if (!p) return;
    const set = p.cells.get(row) ?? new Set<number>();
    if (set.has(i)) return;
    set.add(i);
    p.cells.set(row, set);
    setPaint({ ...p });
  };

  return (
    <div className="snd-steps">
      <div className="snd-view-tools">
        <PatternPicker />
        <label className="snd-inline" title="Pattern length in bars">
          <span className="muted">Bars</span>
          <input
            type="number"
            min={1}
            max={64}
            step={1}
            value={Math.round(bars * 100) / 100}
            onChange={(e) => {
              const b = Math.max(1, Math.min(64, Math.round(Number(e.target.value) || 1)));
              const steps = b * per;
              const drums = Object.fromEntries(rows.map(([k, v]) => [k, withLaneSteps(v, resizeSteps(laneSteps(v), steps, per))]));
              s.update(updatePattern(doc, name, { length: b * beatsPerBar(ts), drums }));
            }}
            aria-label="Pattern length in bars"
          />
        </label>
        <span className="tool-caption muted">Click: hit · Shift: accent · Alt: ghost</span>
        <span className="bar-spacer" />
        <label className="snd-inline">
          <select value={newRow} onChange={(e) => setNewRow(e.target.value)} aria-label="Drum row to add">
            <option value="">Add row…</option>
            {unused.map((k) => (
              <option key={k} value={k}>
                {k} ({GM_DRUMS[k]})
              </option>
            ))}
            <option value="__custom">Custom / MIDI key…</option>
          </select>
        </label>
        <button
          type="button"
          className="tb-btn"
          disabled={!newRow}
          onClick={async () => {
            let row = newRow;
            if (row === '__custom') row = (await promptText('Row name or MIDI key (e.g. 37, cowbell)', ''))?.trim() ?? '';
            setNewRow('');
            const cur = useSound.getState().doc;
            if (!row || !cur || cur.patterns?.[name]?.drums?.[row] !== undefined) return;
            s.update(setDrumRow(cur, name, row, '.'.repeat(count)));
            if (gmDrumKey(row) === null && !/^\d+$/.test(row) && noteNameToMidi(row) === null) logEditor('warn', `Drum lane "${row}" is not a GM drum name, MIDI key or note name; the renderer will reject it.`);
          }}
        >
          <Icon name="plus" size={12} /> Add
        </button>
      </div>
      <div className="snd-steps-scroll">
        {rows.length === 0 && <div className="pane-empty muted">No drum rows. Add kick, snare, hat… with “Add row”.</div>}
        <div className="snd-steps-grid" role="grid" aria-label={`Drum steps of ${name}`}>
          {rows.map(([row, text]) => {
            const steps = parseSteps(resizeSteps(laneSteps(text), count, per), per);
            const pending = paint?.cells.get(row);
            return (
              <div key={row} className="snd-steps-row" role="row">
                <div className="snd-steps-head" role="rowheader">
                  <button type="button" className="ibtn" title={`Preview ${row}`} aria-label={`Preview ${row}`} onClick={() => soundPlayer.previewHit()}>
                    <Icon name="play" size={11} />
                  </button>
                  <span className="snd-row-name" title={GM_DRUMS[row] !== undefined ? `GM key ${GM_DRUMS[row]}` : row}>
                    {row}
                  </span>
                  <button type="button" className="ibtn" title={`Remove ${row}`} aria-label={`Remove row ${row}`} onClick={() => s.update(setDrumRow(doc, name, row, undefined))}>
                    <Icon name="x" size={11} />
                  </button>
                </div>
                {steps.map((v, i) => {
                  const shown = pending?.has(i) ? paint!.value : v;
                  return (
                    <button
                      key={i}
                      type="button"
                      role="gridcell"
                      className={`snd-step v-${shown === 'X' ? 'accent' : shown === 'x' ? 'hit' : shown === '-' ? 'ghost' : shown === '.' ? 'off' : 'vel'}${i % perBeat === 0 ? ' is-beat' : ''}${i % per === 0 ? ' is-bar' : ''}`}
                      style={shown !== '.' && shown !== 'x' && shown !== 'X' && shown !== '-' ? { opacity: 0.35 + 0.65 * stepVelocity(shown) } : undefined}
                      aria-pressed={shown !== '.'}
                      aria-label={`${row} step ${i + 1}: ${shown === '.' ? 'off' : shown === 'X' ? 'accent' : shown === '-' ? 'ghost' : shown === 'x' ? 'hit' : `velocity ${shown}/9`}`}
                      onPointerDown={(e) => {
                        if (e.button !== 0) return;
                        e.preventDefault();
                        const want: StepValue = e.shiftKey ? 'X' : e.altKey ? '-' : 'x';
                        const value: StepValue = v === want ? '.' : want;
                        if (value !== '.') soundPlayer.previewHit(value === 'X' ? 1 : value === '-' ? 0.35 : 0.7);
                        paintRef.current = { value, cells: new Map() };
                        addCell(row, i);
                      }}
                      onPointerEnter={() => addCell(row, i)}
                    />
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
